import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X, Hash, Megaphone, Lock, Users, Plus, ArrowLeft, Search } from 'lucide-react';
import { ChannelService, channelToChat } from '../../services/ChannelService';

/** The icon that says what kind of channel this is — shared with the chat list. */
export const ChannelGlyph = ({ kind, visibility, size = 16, className = '' }) => {
    if (kind === 'announcement') return <Megaphone size={size} className={className} aria-hidden="true" />;
    if (visibility === 'private' || kind === 'restricted') return <Lock size={size} className={className} aria-hidden="true" />;
    return <Hash size={size} className={className} aria-hidden="true" />;
};

const KIND_LABEL = {
    announcement: 'Announcements', organization: 'Everyone', department: 'Department',
    project: 'Project', restricted: 'Restricted', incident: 'Incident',
};

const Row = ({ c, action }) => (
    <div className="flex items-start gap-3 px-4 py-3 border-b border-slate-100 last:border-b-0">
        <div className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0">
            <ChannelGlyph kind={c.kind} visibility={c.visibility} />
        </div>
        <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0">
                <span className="text-sm font-semibold text-slate-800 truncate">{c.name}</span>
                <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 rounded-full px-1.5 py-0.5 flex-shrink-0">{KIND_LABEL[c.kind] || c.kind}</span>
                {c.archived && <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 rounded-full px-1.5 py-0.5 flex-shrink-0">Archived</span>}
            </div>
            {c.purpose && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{c.purpose}</p>}
            <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1">
                <Users size={11} aria-hidden="true" /> {c.memberCount} {c.memberCount === 1 ? 'member' : 'members'}
                {c.postingPolicy === 'admins' && <span>· only admins post</span>}
            </p>
        </div>
        {action}
    </div>
);

/**
 * Browse, join and (for managers and admins) create channels.
 * All of it goes through the proxy; this component only displays and asks.
 */
const ChannelDirectoryModal = ({ isOpen, onClose, onOpenChannel, allUsers = [], currentUserId }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(null);
    const [tab, setTab] = useState('mine');
    const [tpl, setTpl] = useState(null);          // chosen template while creating
    const [form, setForm] = useState({ name: '', purpose: '', reviewDate: '', memberIds: [] });
    const [memberQuery, setMemberQuery] = useState('');

    const load = useCallback(async () => {
        setError(null);
        try { setData(await ChannelService.list()); } catch (e) { setError(e.message); }
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        setData(null); setTab('mine'); setTpl(null); load();
    }, [isOpen, load]);

    const open = (c) => { onOpenChannel?.(channelToChat(c)); onClose?.(); };

    const join = async (c) => {
        setBusy(c.id); setError(null);
        try { open(await ChannelService.join(c.id)); } catch (e) { setError(e.message); } finally { setBusy(null); }
    };

    const pickTemplate = (t) => {
        setTpl(t);
        setForm({ name: t.defaultName || '', purpose: t.defaultPurpose || '', reviewDate: '', memberIds: [] });
        setMemberQuery('');
    };

    // People who may be added: active staff, minus me, and within a restricted template's roles.
    const candidates = useMemo(() => {
        if (!tpl || tpl.membership === 'all_staff') return [];
        const q = memberQuery.trim().toLowerCase();
        return (allUsers || [])
            .filter(u => u && u.id && u.id !== currentUserId && u.role && u.isActive !== false)
            .filter(u => !tpl.allowedRoles || tpl.allowedRoles.includes(String(u.role).toLowerCase()))
            .filter(u => !q || `${u.name || ''} ${u.email || ''} ${u.role || ''}`.toLowerCase().includes(q))
            .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    }, [tpl, allUsers, currentUserId, memberQuery]);

    const toggleMember = (uid) => setForm(f => ({
        ...f, memberIds: f.memberIds.includes(uid) ? f.memberIds.filter(x => x !== uid) : [...f.memberIds, uid],
    }));

    const create = async (e) => {
        e.preventDefault();
        setBusy('create'); setError(null);
        try {
            const c = await ChannelService.create({
                templateId: tpl.id, name: form.name, purpose: form.purpose,
                memberIds: form.memberIds, reviewDate: form.reviewDate || undefined,
            });
            open(c);
        } catch (err) { setError(err.message); } finally { setBusy(null); }
    };

    if (!isOpen) return null;

    const tabs = [
        { key: 'mine', label: 'Your channels' },
        { key: 'browse', label: `Browse${data?.discoverable?.length ? ` (${data.discoverable.length})` : ''}` },
        ...(data?.canCreate ? [{ key: 'create', label: 'Create' }] : []),
    ];

    return (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4" onClick={onClose}>
            <div
                role="dialog" aria-modal="true" aria-label="Channels"
                className="w-full max-w-lg max-h-[85vh] flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => { if (e.key === 'Escape') onClose?.(); }}
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                    <h2 className="text-base font-bold text-slate-800 flex items-center gap-2"><Hash size={18} className="text-indigo-500" /> Channels</h2>
                    <button onClick={onClose} className="p-1.5 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Close"><X size={18} /></button>
                </div>

                <div className="flex gap-1 px-4 pt-3" role="tablist">
                    {tabs.map(t => (
                        <button
                            key={t.key} role="tab" aria-selected={tab === t.key}
                            onClick={() => { setTab(t.key); setTpl(null); }}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold ${tab === t.key ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:bg-slate-100'}`}
                        >{t.label}</button>
                    ))}
                </div>

                {error && <p className="mx-5 mt-3 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" role="alert">{error}</p>}

                <div className="flex-1 overflow-y-auto custom-scrollbar mt-2">
                    {!data && !error && <p className="px-5 py-8 text-center text-sm text-slate-400">Loading channels…</p>}

                    {data && tab === 'mine' && (
                        data.mine.length === 0
                            ? <p className="px-5 py-8 text-center text-sm text-slate-400">You are not in any channels yet.</p>
                            : data.mine.map(c => (
                                <Row key={c.id} c={c} action={
                                    <button onClick={() => open(c)} className="text-xs font-semibold text-indigo-600 hover:bg-indigo-50 rounded-lg px-2.5 py-1.5 flex-shrink-0">Open</button>
                                } />
                            ))
                    )}

                    {data && tab === 'browse' && (
                        data.discoverable.length === 0
                            ? <p className="px-5 py-8 text-center text-sm text-slate-400">No other open channels to join.</p>
                            : data.discoverable.map(c => (
                                <Row key={c.id} c={c} action={
                                    <button disabled={busy === c.id} onClick={() => join(c)} className="text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 rounded-lg px-2.5 py-1.5 flex-shrink-0">
                                        {busy === c.id ? 'Joining…' : 'Join'}
                                    </button>
                                } />
                            ))
                    )}

                    {data && tab === 'create' && !tpl && (
                        <div className="p-4 grid gap-2">
                            <p className="text-xs text-slate-500 px-1">Start from a template. Channels are created only when you need them.</p>
                            {data.templates.map(t => (
                                <button key={t.id} onClick={() => pickTemplate(t)} className="flex items-start gap-3 text-left p-3 rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/40">
                                    <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0">
                                        <ChannelGlyph kind={t.kind} visibility={t.visibility} />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-sm font-semibold text-slate-800">{t.label}</div>
                                        <div className="text-[11px] text-slate-500">
                                            {t.membership === 'all_staff' ? 'Everyone, automatically' : t.visibility === 'open' ? 'Anyone can join' : 'Invite only'}
                                            {t.postingPolicy === 'admins' ? ' · only admins post' : ''}
                                            {t.allowedRoles ? ' · managers & admins only' : ''}
                                            {t.needsReviewDate ? ' · has a review date' : ''}
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}

                    {data && tab === 'create' && tpl && (
                        <form onSubmit={create} className="p-5 grid gap-3">
                            <button type="button" onClick={() => setTpl(null)} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 w-fit">
                                <ArrowLeft size={14} /> {tpl.label}
                            </button>
                            <label className="grid gap-1 text-xs font-semibold text-slate-600">
                                Name
                                <input required minLength={2} maxLength={60} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                    className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                            </label>
                            <label className="grid gap-1 text-xs font-semibold text-slate-600">
                                Purpose
                                <textarea rows={2} maxLength={300} value={form.purpose} onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))}
                                    className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-200 resize-none" />
                            </label>
                            {tpl.needsReviewDate && (
                                <label className="grid gap-1 text-xs font-semibold text-slate-600">
                                    Review date — archive or extend it then
                                    <input type="date" required value={form.reviewDate} onChange={e => setForm(f => ({ ...f, reviewDate: e.target.value }))}
                                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal text-slate-800" />
                                </label>
                            )}
                            {tpl.membership === 'all_staff' ? (
                                <p className="text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2">Every active staff member is added automatically, including people who join later.</p>
                            ) : (
                                <div className="grid gap-1.5">
                                    <span className="text-xs font-semibold text-slate-600">Members ({form.memberIds.length} chosen, plus you)</span>
                                    <div className="relative">
                                        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <input value={memberQuery} onChange={e => setMemberQuery(e.target.value)} placeholder="Find people"
                                            className="w-full rounded-lg border border-slate-200 pl-8 pr-3 py-1.5 text-sm" aria-label="Find people to add" />
                                    </div>
                                    <div className="max-h-40 overflow-y-auto custom-scrollbar rounded-lg border border-slate-100">
                                        {candidates.length === 0 && <p className="px-3 py-2 text-xs text-slate-400">No one matches.</p>}
                                        {candidates.map(u => (
                                            <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer">
                                                <input type="checkbox" checked={form.memberIds.includes(u.id)} onChange={() => toggleMember(u.id)} />
                                                <span className="truncate">{u.name || u.email}</span>
                                                <span className="ml-auto text-[10px] text-slate-400">{u.role}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <button type="submit" disabled={busy === 'create'} className="mt-1 flex items-center justify-center gap-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold py-2.5">
                                <Plus size={16} /> {busy === 'create' ? 'Creating…' : 'Create channel'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChannelDirectoryModal;
